import { Group } from '@mantine/core';
import { nanoid } from 'nanoid';
import { DeviceListResponse } from 'api/actions/device-list/device-list-response';
import P4Medium from 'components/typography/P4Medium';
import { forwardRef, useCallback, useMemo } from 'react';
import {
  DataSelect,
  DataSelectInheritedProps,
  DataMultiSelectInheritedProps,
  DataMultiSelect,
} from 'components/selects/DataSelect';
import { useApi } from 'api/api-context';

const EMPTY_DEVICE_IDS: number[] = [];

interface ItemProps extends React.ComponentPropsWithoutRef<'div'> {
  item?: DeviceListResponse[number] & {
    label: string;
    value: string;
    _displayLabel: string;
  };
  label: string;
}

const DeviceSelectItem = forwardRef<HTMLDivElement, ItemProps>(({ item, label, ...others }: ItemProps, ref) => {
  return (
    <div ref={ref} {...others}>
      {item ? (
        <Group noWrap spacing={4} align="baseline">
          <P4Medium c="gray.4">(#{item.deviceId})</P4Medium>
          {item._displayLabel}
        </Group>
      ) : (
        label
      )}
    </div>
  );
});

interface AdditionalProps {
  departmentId?: number;
  showDiscarded?: boolean;
  deviceSubtypeId?: number;
  onlyParents?: boolean;
  excludeParents?: boolean;
  excludeDeviceIds?: number[];
  canAssignPriceOffers?: boolean;
}

/**
 * Fetches devices from API.
 */
function useDeviceSelect({
  departmentId,
  deviceSubtypeId,
  showDiscarded = true,
  onlyParents = false,
  excludeParents = false,
  excludeDeviceIds = EMPTY_DEVICE_IDS,
  canAssignPriceOffers,
}: AdditionalProps = {}) {
  const { getAction } = useApi();
  const excludeDeviceIdsKey = excludeDeviceIds.join(',');
  const excludedDeviceIds = useMemo(() => new Set(excludeDeviceIds), [excludeDeviceIdsKey]);

  const fetchDevices = useCallback(async () => {
    const action = getAction('DeviceList');

    const devices = await action({
      query: {
        filters: {
          'departmentId.eq': departmentId === -1 ? undefined : departmentId,
          'deviceSubtypeId.in': [deviceSubtypeId],
          'showDiscarded.eq': showDiscarded !== undefined && showDiscarded ? 1 : 0,
          'deviceStructure.eq': onlyParents ? 'parent' : undefined,
          'deviceStructure.ne': excludeParents ? 'parent' : undefined,
          'canAssignPriceOffers.eq': canAssignPriceOffers ? 1 : undefined,
        },
      },
    });

    const filtered = devices.reduce<
      Array<DeviceListResponse[number] & { _displayLabel: string; label: string; value: string; deviceId: number }>
    >((result, device) => {
      if (!device || excludedDeviceIds.has(device.deviceId)) {
        return result;
      }

      const deviceLabel =
        (device.status ? '' : ' [VYRADENÉ] ') +
        (device.parentDevice ? device.parentDevice.deviceName + ' / ' : '') +
        device.deviceName;

      result.push({
        ...device,
        _displayLabel: deviceLabel,
        label: `(#${device.deviceId}) ` + deviceLabel,
        value: String(device.deviceId),
      });

      return result;
    }, []);

    return filtered;
  }, [
    getAction,
    departmentId,
    deviceSubtypeId,
    showDiscarded,
    onlyParents,
    excludeParents,
    excludedDeviceIds,
    canAssignPriceOffers,
  ]);

  return useMemo(() => ({ fetchDevices }), [fetchDevices]);
}

/**
 * Parameters of the DeviceSelect component.
 */
type DeviceSelectProps = DataSelectInheritedProps & AdditionalProps;

/**
 * Parameters of the DeviceMultiSelect component.
 */
type DeviceMultiSelectProps = DataMultiSelectInheritedProps & AdditionalProps;

/**
 * Select for devices.
 */
export function DeviceSelect({
  departmentId,
  deviceSubtypeId,
  showDiscarded,
  onlyParents,
  excludeParents,
  excludeDeviceIds = EMPTY_DEVICE_IDS,
  canAssignPriceOffers,
  ...props
}: DeviceSelectProps) {
  const excludeDeviceIdsKey = excludeDeviceIds.join(',');
  const key = useMemo(
    () => nanoid(),
    [
      departmentId,
      deviceSubtypeId,
      showDiscarded,
      onlyParents,
      excludeParents,
      excludeDeviceIdsKey,
      canAssignPriceOffers,
    ]
  );

  const { fetchDevices } = useDeviceSelect({
    departmentId,
    deviceSubtypeId,
    showDiscarded,
    onlyParents,
    excludeParents,
    excludeDeviceIds,
    canAssignPriceOffers,
  });

  return (
    <DataSelect
      key={key}
      valueProp="value"
      labelProp="label"
      itemComponent={DeviceSelectItem}
      action={fetchDevices}
      {...props}
    />
  );
}

/**
 * Multi-select for devices.
 */
export function DeviceMultiSelect({
  departmentId,
  deviceSubtypeId,
  showDiscarded,
  onlyParents,
  excludeParents,
  excludeDeviceIds = EMPTY_DEVICE_IDS,
  canAssignPriceOffers,
  ...props
}: DeviceMultiSelectProps) {
  const excludeDeviceIdsKey = excludeDeviceIds.join(',');
  const key = useMemo(
    () => nanoid(),
    [
      departmentId,
      deviceSubtypeId,
      showDiscarded,
      onlyParents,
      excludeParents,
      excludeDeviceIdsKey,
      canAssignPriceOffers,
    ]
  );

  const { fetchDevices } = useDeviceSelect({
    departmentId,
    deviceSubtypeId,
    showDiscarded,
    onlyParents,
    excludeParents,
    excludeDeviceIds,
    canAssignPriceOffers,
  });

  return (
    <DataMultiSelect
      key={key}
      valueProp="value"
      labelProp="label"
      itemComponent={DeviceSelectItem}
      action={fetchDevices}
      {...props}
    />
  );
}
